import {
  ADDITIONAL_DEPRECIATION_YEARS,
  DEFAULT_RENT_INCREASE_INTERVAL_MONTHS,
  INVESTMENT_HORIZON_YEARS,
  MAX_FINANCING_SHARE_PCT,
  PROPERTY_STATES,
  calculateInvestment,
  equityReturn,
  formatEuro,
  formatEuroExact,
  formatInteger,
  monthlyBalance,
  propertyStateLabels,
  propertyStateShortLabels,
  toBerlinDateKey,
  type EquityReturn,
  type InvestmentResult,
  type InvestmentYear,
} from '@brimo/shared';
import type { CalculatorContext, InvestmentInputValues } from '../types';

/**
 * The view model of the Immobilienrechner: `calculateInvestment`, `monthlyBalance` and `equityReturn` of
 * `@brimo/shared` (engine v2, Nico Kremer's reference calculator) plus the figures of the advisor view
 * („Berateransicht", blocks 1–7) and the customer view („Kundenansicht", the nine questions of
 * design/designs/BRIMO Immobilienrechner Screens.dc.html). Money arrives in cents; amounts are shown in whole
 * euros like the prototype.
 */

const NBSP = ' ';

/** 49800000 → "498.000 €" — whole euros, as the prototype's `eur()`. */
export function eur(cents: number): string {
  return formatEuro(Math.round(cents / 100) * 100);
}

/** 1311 → "13,11 €" — two decimals (`eur(n, 2)`). */
export function eur2(cents: number): string {
  return formatEuroExact(Math.round(cents));
}

/** −87650 → "−877" · 126400 → "+1.264" — signed whole euros without the unit (`sgn()`). */
export function sgn(cents: number): string {
  const euros = Math.round(cents / 100);
  const sign = euros > 0 ? '+' : euros < 0 ? '−' : '';
  return `${sign}${formatInteger(Math.abs(euros))}`;
}

/** 2 → "2,0 %" · 3.6 → "3,60 %" (with `decimals` 2) — the prototype's fixed-decimal `pct()`. */
export function pct(value: number, decimals = 1): string {
  return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value)}${NBSP}%`;
}

/** A rate that may be undefined (`equityReturn` without invested capital) → "—". */
export function pctOrDash(value: number | null, decimals = 1): string {
  return value === null ? '—' : pct(value, decimals);
}

// --- parameters ---------------------------------------------------------------------------------------

/** The three deductible running costs the inputs show separately; the engine only sees their sum. */
export interface CostSplit {
  sevCents: number;
  wegCents: number;
  otherCostsCents: number;
}

export type CostSplitKey = keyof CostSplit;
export type InputKey = keyof InvestmentInputValues;
export type ParamKey = InputKey | CostSplitKey;

export const COST_SPLIT_KEYS: readonly CostSplitKey[] = ['sevCents', 'wegCents', 'otherCostsCents'];

export function isCostSplitKey(key: ParamKey): key is CostSplitKey {
  return (COST_SPLIT_KEYS as readonly string[]).includes(key);
}

export interface SliderRange {
  min: number;
  max: number;
  step: number;
}

export interface ParamSpec {
  key: ParamKey;
  label: string;
  /** `cents` fields are edited in euros; `years`/`months`/`count` are integers. */
  unit: 'cents' | 'pct' | 'years' | 'months' | 'count';
  /** Step of the typed field (arrow keys) in the edited unit. */
  step: number;
  min: number;
  max?: number | undefined;
  /** The slider next to the field; a function when its range follows the current value. */
  slider?: SliderRange | ((values: InvestmentInputValues) => SliderRange) | undefined;
}

/** Slider range that grows with the value instead of clamping a unit outside the usual range. */
function growingRange(
  key: InputKey,
  floorMax: number,
  step: number,
): (values: InvestmentInputValues) => SliderRange {
  return (values) => {
    const euros = (values[key] as number) / 100;
    return { min: 0, max: Math.max(floorMax, Math.ceil((euros * 1.5) / step) * step), step };
  };
}

export const OBJECT_PARAMS: ParamSpec[] = [
  {
    key: 'purchasePriceCents',
    label: 'Kaufpreis',
    unit: 'cents',
    step: 1000,
    min: 0,
    slider: growingRange('purchasePriceCents', 700_000, 5000),
  },
  {
    key: 'acquisitionCostsPct',
    label: 'Kaufnebenkosten',
    unit: 'pct',
    step: 0.01,
    min: 0,
    max: 30,
    slider: { min: 5, max: 12, step: 0.01 },
  },
  { key: 'renovationCents', label: 'Renovierungskosten', unit: 'cents', step: 500, min: 0 },
  { key: 'renovationSubsidyCents', label: 'Renovierungszuschuss', unit: 'cents', step: 500, min: 0 },
  { key: 'maintenanceExpenseCents', label: 'Erhaltungsaufwand einmalig', unit: 'cents', step: 500, min: 0 },
];

export const EQUITY_PARAM: ParamSpec = {
  key: 'equityCents',
  label: 'Eigenkapital',
  unit: 'cents',
  step: 1000,
  min: 0,
  slider: (values) => ({ min: 0, max: equityMaxEuros(values), step: 5000 }),
};

/**
 * „Finanzierungsanteil" in % of the purchase price (Nico's `finanzanteil`, D-347). The stored value is null while the
 * equity decides the loan; the field then shows the share that follows from the equity.
 */
export const FINANCING_SHARE_PARAM: ParamSpec = {
  key: 'financingSharePct',
  label: 'Finanzierungsanteil',
  unit: 'pct',
  step: 1,
  min: 0,
  max: MAX_FINANCING_SHARE_PCT,
  slider: { min: 0, max: MAX_FINANCING_SHARE_PCT, step: 1 },
};

/** „AfA manuell" per year (Nico's `afaManuell`, D-347); shown while the switch is on. */
export const DEPRECIATION_MANUAL_PARAM: ParamSpec = {
  key: 'depreciationManualCents',
  label: 'AfA-Betrag p.a.',
  unit: 'cents',
  step: 100,
  min: 0,
};

/** „Zusatz-AfA" per year, years 1–10 (Nico's `zusatzAfa`, D-347). */
export const ADDITIONAL_DEPRECIATION_PARAM: ParamSpec = {
  key: 'additionalDepreciationCents',
  label: `Zusatz-AfA p.a. (Jahre 1–${ADDITIONAL_DEPRECIATION_YEARS})`,
  unit: 'cents',
  step: 100,
  min: 0,
};

/** The (i) explanations of Nico's inputs (D-347). */
export const INPUT_INFO = {
  financingSharePct:
    'Anteil des Kaufpreises, der finanziert wird. Über 100 % finanziert das Darlehen auch Kaufnebenkosten und Renovierung mit; das Eigenkapital ist dann 0 €. Ändern Sie das Eigenkapital, gilt wieder der Anteil, der sich daraus ergibt.',
  repaymentAsCost:
    'Eingeschaltet zählt die Tilgung zum monatlichen Eigenanteil. Ausgeschaltet gilt sie als Vermögensaufbau und nicht als Kosten; Cashflow und Eigenkapitalrendite rechnen die Tilgung immer mit.',
  parkingSpaceRentCents:
    'Miete für Stellplatz oder Garage pro Monat. Sie kommt zur Soll-Miete und zur Kaltmiete (Ist) hinzu und steigt mit der Mietsteigerung.',
  depreciationManual:
    'Fester AfA-Betrag pro Jahr statt AfA-Satz × Gebäudeanteil, z. B. aus einem Gutachten zur Restnutzungsdauer. Er gilt in jedem Jahr der Betrachtung.',
  additionalDepreciationCents: `Zusätzliche Abschreibung pro Jahr in den Jahren 1 bis ${ADDITIONAL_DEPRECIATION_YEARS}, z. B. Sonder- oder Denkmal-AfA. Sie mindert das steuerliche Ergebnis zusätzlich zur AfA.`,
} as const;

export const FINANCING_PARAMS: ParamSpec[] = [
  {
    key: 'interestPct',
    label: 'Sollzins',
    unit: 'pct',
    step: 0.05,
    min: 0,
    max: 15,
    slider: { min: 1, max: 7, step: 0.05 },
  },
  {
    key: 'repaymentPct',
    label: 'Anfangstilgung',
    unit: 'pct',
    step: 0.1,
    min: 0,
    max: 15,
    slider: { min: 1, max: 6, step: 0.1 },
  },
  {
    key: 'fixedInterestYears',
    label: 'Zinsbindung',
    unit: 'years',
    step: 1,
    min: 1,
    max: INVESTMENT_HORIZON_YEARS,
    slider: { min: 1, max: INVESTMENT_HORIZON_YEARS, step: 1 },
  },
  { key: 'followUpInterestPct', label: 'Anschlusszins', unit: 'pct', step: 0.05, min: 0, max: 15 },
  { key: 'followUpRepaymentPct', label: 'Anschlusstilgung', unit: 'pct', step: 0.1, min: 0, max: 15 },
  {
    key: 'unscheduledRepaymentCents',
    label: 'Sondertilgung p.a.',
    unit: 'cents',
    step: 500,
    min: 0,
    slider: { min: 0, max: 12_000, step: 500 },
  },
];

export const RENT_PARAMS: ParamSpec[] = [
  {
    key: 'rentCents',
    label: 'Soll-Miete / Monat',
    unit: 'cents',
    step: 10,
    min: 0,
    slider: growingRange('rentCents', 2000, 10),
  },
  {
    key: 'rentTaxCents',
    label: 'Kaltmiete (Ist) / Monat',
    unit: 'cents',
    step: 10,
    min: 0,
    slider: growingRange('rentTaxCents', 2000, 10),
  },
  {
    key: 'parkingSpaceRentCents',
    label: 'Stellplatzmiete / Monat',
    unit: 'cents',
    step: 5,
    min: 0,
    slider: growingRange('parkingSpaceRentCents', 200, 5),
  },
  { key: 'rentSubsidyCents', label: 'Mietsubvention einmalig', unit: 'cents', step: 100, min: 0 },
  { key: 'rentSubsidyCycles', label: 'Subventionszyklen', unit: 'count', step: 1, min: 0, max: 10 },
];

export const RENT_INCREASE_PARAM: ParamSpec = {
  key: 'rentIncreasePct',
  label: 'Mietsteigerung',
  unit: 'pct',
  step: 0.5,
  min: 0,
  max: 100,
};

export const RENT_INTERVAL_PARAM: ParamSpec = {
  key: 'rentIncreaseIntervalMonths',
  label: '… alle N Monate',
  unit: 'months',
  step: 1,
  min: 0,
  max: 120,
};

export const COST_PARAMS: ParamSpec[] = [
  { key: 'sevCents', label: 'SE-Verwaltung (absetzbar)', unit: 'cents', step: 1, min: 0 },
  { key: 'wegCents', label: 'WEG-Verwaltung (absetzbar)', unit: 'cents', step: 1, min: 0 },
  {
    key: 'otherCostsCents',
    label: 'Sonstige nicht umlagefähige Kosten (absetzbar)',
    unit: 'cents',
    step: 1,
    min: 0,
  },
  {
    key: 'nonDeductibleCostsCents',
    label: 'Instandhaltungsrücklage (nicht absetzbar)',
    unit: 'cents',
    step: 1,
    min: 0,
  },
];

export const ASSUMPTION_PARAMS: ParamSpec[] = [
  {
    key: 'appreciationPct',
    label: 'Wertentwicklung p.a.',
    unit: 'pct',
    step: 0.1,
    min: 0,
    max: 20,
    slider: { min: 0, max: 6, step: 0.1 },
  },
  {
    key: 'depreciationPct',
    label: 'AfA linear',
    unit: 'pct',
    step: 0.05,
    min: 0,
    max: 10,
    slider: { min: 0, max: 10, step: 0.05 },
  },
  {
    key: 'buildingSharePct',
    label: 'Gebäudeanteil',
    unit: 'pct',
    step: 1,
    min: 0,
    max: 100,
    slider: { min: 0, max: 100, step: 1 },
  },
  {
    key: 'taxRatePct',
    label: 'Persönlicher Steuersatz',
    unit: 'pct',
    step: 1,
    min: 0,
    max: 60,
    slider: { min: 0, max: 60, step: 1 },
  },
  {
    key: 'saleYear',
    label: 'Verkaufsjahr',
    unit: 'years',
    step: 1,
    min: 1,
    max: INVESTMENT_HORIZON_YEARS,
    slider: { min: 1, max: INVESTMENT_HORIZON_YEARS, step: 1 },
  },
];

/** Stored value (cents for money) → edited value (euros for money). */
export function toEditValue(spec: Pick<ParamSpec, 'unit'>, value: number): number {
  return spec.unit === 'cents' ? value / 100 : value;
}

/** Edited value → stored value; integers for years, months and counts. */
export function fromEditValue(spec: Pick<ParamSpec, 'unit'>, value: number): number {
  if (spec.unit === 'cents') return Math.round(value * 100);
  if (spec.unit === 'pct') return value;
  return Math.round(value);
}

/** Clamps an edited value to the spec's bounds. */
export function clampEditValue(spec: Pick<ParamSpec, 'min' | 'max'>, value: number): number {
  const lower = Math.max(spec.min, value);
  return spec.max === undefined ? lower : Math.min(spec.max, lower);
}

export function sliderRange(spec: ParamSpec, values: InvestmentInputValues): SliderRange | null {
  if (!spec.slider) return null;
  return typeof spec.slider === 'function' ? spec.slider(values) : spec.slider;
}

/** The financing share the inputs give: the set share, else loan ÷ price from the equity input (0 without a price). */
export function financingShareOf(values: InvestmentInputValues): number {
  if (values.financingSharePct !== null) return values.financingSharePct;
  const price = values.purchasePriceCents;
  if (price <= 0) return 0;
  const invest =
    price * (1 + values.acquisitionCostsPct / 100) +
    Math.max(0, values.renovationCents - values.renovationSubsidyCents);
  return (Math.max(0, invest - values.equityCents) / price) * 100;
}

/** Equity that follows from a set financing share (Nico's `max(0, gesamtinvest − fin)`), else the equity input. */
export function equityOf(values: InvestmentInputValues): number {
  if (values.financingSharePct === null) return values.equityCents;
  const price = values.purchasePriceCents;
  const invest =
    price * (1 + values.acquisitionCostsPct / 100) +
    Math.max(0, values.renovationCents - values.renovationSubsidyCents);
  return Math.max(0, Math.round(invest - (price * values.financingSharePct) / 100));
}

/** The linear AfA per year (base × rate) — the starting value when the manual AfA is switched on. */
export function linearDepreciationCents(values: InvestmentInputValues): number {
  const acquisition = values.sellerPaysAcquisitionCosts
    ? values.purchasePriceCents / (1 - values.acquisitionCostsPct / 100) - values.purchasePriceCents
    : (values.purchasePriceCents * values.acquisitionCostsPct) / 100;
  const base = ((values.purchasePriceCents + acquisition) * values.buildingSharePct) / 100;
  return Math.round((base * values.depreciationPct) / 100);
}

export function equityMaxEuros(values: InvestmentInputValues): number {
  const invest =
    (values.purchasePriceCents / 100) * (1 + values.acquisitionCostsPct / 100) +
    Math.max(0, values.renovationCents - values.renovationSubsidyCents) / 100;
  return Math.max(5000, Math.round(invest / 5000) * 5000);
}

/** SE-Verwaltung + WEG-Verwaltung + other costs → the engine's `deductibleCostsCents`. */
export function deductibleSum(split: CostSplit): number {
  return split.sevCents + split.wegCents + split.otherCostsCents;
}

/**
 * The split seeded from the context: the API resolves SE-Verwaltung, WEG-Verwaltung and the other costs from the
 * unit, its TeamProQ cost items or the organization's €/m² default (`costs`, D-343); they add up to
 * `defaults.deductibleCostsCents`.
 */
export function costSplitFromContext(context: CalculatorContext): CostSplit {
  return {
    sevCents: context.costs.sevCents,
    wegCents: context.costs.wegCents,
    otherCostsCents: context.costs.otherCostsCents,
  };
}

/** Hint of an input whose default is not a value of the unit (D-343). */
export const FALLBACK_HINT = 'Standardwert – an der Einheit nicht gepflegt';
/** The API's source label of a fixed standard value (`CALCULATOR_SOURCE_STANDARD`). */
const STANDARD_SOURCE = 'Standardwert';

/** The API's source of a value from the property's „Objekt-Vorgaben" (`CALCULATOR_SOURCE_PROPERTY`, D-359). */
export const PROPERTY_SOURCE = 'aus Objekt-Vorgaben';
/** The API's source of an organization value (`CALCULATOR_SOURCE_ORGANIZATION`). */
const ORGANIZATION_SOURCE = 'Organisationsvorgabe';
/** Where the organization's calculator assumptions are changed (Einstellungen › Rechenannahmen). */
export const ORGANIZATION_ASSUMPTIONS_PATH = '/settings/organization/calculation-assumptions';

/** Inputs the property's „Objekt-Vorgaben" can set (D-359): their origin is always named. */
const INHERITED_KEYS: readonly ParamKey[] = [
  'depreciationPct',
  'buildingSharePct',
  'interestPct',
  'repaymentPct',
  'sevCents',
  'wegCents',
  'otherCostsCents',
  'nonDeductibleCostsCents',
];

/**
 * Source line under an input: „Standardwert – an der Einheit nicht gepflegt (· <Quelle>)" when the context fell
 * back to a standard or organization value; the composition of the acquisition costs, a TeamProQ cost item and
 * the property's „Objekt-Vorgaben" as the origin are shown too, and for the inherited inputs also an organization
 * value that is no fallback (D-360); otherwise null.
 */
export function sourceHint(context: CalculatorContext, key: ParamKey): string | null {
  const entry = context.sources.find((source) => source.field === key);
  if (!entry) return null;
  if (entry.fallback) {
    return entry.source === STANDARD_SOURCE ? FALLBACK_HINT : `${FALLBACK_HINT} · ${entry.source}`;
  }
  if (entry.source === PROPERTY_SOURCE) return PROPERTY_SOURCE;
  if (entry.source === ORGANIZATION_SOURCE && INHERITED_KEYS.includes(key)) return ORGANIZATION_SOURCE;
  return key === 'acquisitionCostsPct' || entry.source.startsWith('TeamProQ-Kostenposition')
    ? entry.source
    : null;
}

/**
 * Where the „ · ändern" link after a shown source line leads (D-360), null without a link: the property's
 * „Objekt-Vorgaben" (also for a standard value of an inherited input — it is set there), the organization's
 * assumptions, the unit for its own figures (target rent, TeamProQ cost items). Only for users who may change
 * the target (`context.editLinks`).
 */
export function sourceLink(context: CalculatorContext, key: ParamKey): string | null {
  const entry = context.sources.find((source) => source.field === key);
  if (!entry || sourceHint(context, key) === null) return null;
  const { unit, editLinks } = context;
  const propertyPath = `/properties/${unit.propertyId}?calc=1`;
  const unitPath = `/properties/${unit.propertyId}/units/${unit.id}`;
  if (entry.source === PROPERTY_SOURCE) return editLinks.property ? propertyPath : null;
  if (entry.source.includes(ORGANIZATION_SOURCE))
    return editLinks.organization ? ORGANIZATION_ASSUMPTIONS_PATH : null;
  if (entry.source === STANDARD_SOURCE && INHERITED_KEYS.includes(key)) {
    return editLinks.property ? propertyPath : null;
  }
  if (
    entry.source.startsWith('TeamProQ-Kostenposition') ||
    entry.source.startsWith('Soll-Miete der Einheit')
  ) {
    return editLinks.unit ? unitPath : null;
  }
  return null;
}

/** Months covered by the rent subsidy: cycles × interval, 36 months per cycle without rent increases (D-342). */
export function subsidyMonths(values: InvestmentInputValues): number {
  const cycle =
    values.rentIncreaseIntervalMonths > 0
      ? values.rentIncreaseIntervalMonths
      : DEFAULT_RENT_INCREASE_INTERVAL_MONTHS;
  return values.rentSubsidyCycles * cycle;
}

/** "Persönlicher Steuersatz" source: the API's `sources` entry, else contact profile / organization default. */
export function taxRateSource(context: CalculatorContext): string {
  const entry = context.sources.find((source) => source.field === 'taxRatePct');
  if (entry) return entry.source;
  return context.contact && context.contact.taxRatePct !== null ? 'Kontaktprofil' : 'Organisationsvorgabe';
}

// --- advisor view (Berateransicht) ---------------------------------------------------------------------

export type Horizon = 10 | 20 | 30;
export const HORIZONS: readonly Horizon[] = [10, 20, 30];

export interface BalanceRow {
  label: string;
  cents: number;
}

export interface MonthlyBalanceView {
  income: BalanceRow[];
  expenses: BalanceRow[];
  incomeTotalCents: number;
  expenseTotalCents: number;
  /** > 0: the investor pays; < 0: surplus. */
  ownShareCents: number;
  headline: string;
  formula: string;
  /** With „Tilgung als Kosten" off: the principal left out of the balance (D-347); else null. */
  repaymentNote: string | null;
}

export interface FinancingView {
  priceCents: number;
  pricePerSqmCents: number | null;
  acquisitionCostsPct: number;
  acquisitionCostsCents: number;
  totalInvestmentCents: number;
  /** Nico's „Anfangsrendite": year-1 rent ÷ total investment. */
  grossYieldPct: number;
  loanCents: number;
  loanNote: string;
  equityCents: number;
  annuityMonthlyCents: number;
  annuityNote: string;
  fixedInterestYears: number;
  fixedInterestNote: string;
  equityRatePaPct: number | null;
}

export interface TaxBlockView {
  baseCents: number;
  /** „AfA manuell" is on: the AfA is a fixed amount, not base × rate (D-347). */
  manualDepreciation: boolean;
  depreciationPct: number;
  usefulLifeYears: number | null;
  depreciationYearCents: number;
  depreciationMonthCents: number;
  taxableYearCents: number;
  taxRatePct: number;
  taxEffectYearCents: number;
  taxEffectMonthCents: number;
  maintenanceExpenseCents: number;
  oneOffRefundCents: number;
  /** „Zusatz-AfA" of year 1 (deducted in years 1–10). */
  additionalDepreciationYearCents: number;
}

export interface TodayView {
  gainCents: number;
  explanation: string;
  propertyValueCents: number;
  remainingDebtCents: number;
  netSaleCents: number;
  investedCents: number;
  equityReturn: EquityReturn;
}

export interface DirectView {
  monthlyPaymentCents: number;
  monthlyPrincipalCents: number;
  netMonthlyCents: number;
  paymentsTotalCents: number;
  repaidTotalCents: number;
  equityWithoutAppreciationCents: number;
  paid: number[];
  repaid: number[];
}

export interface YearlyView {
  debt: number[];
  equity: number[];
  value: number[];
}

export interface SubsidyView {
  rentSubsidyCents: number;
  cycles: number;
  months: number;
  rentSentence: string | null;
  renovationSubsidyCents: number;
  maintenanceExpenseCents: number;
  oneOffRefundCents: number;
}

export interface AdvisorView {
  horizon: Horizon;
  balance: MonthlyBalanceView;
  financing: FinancingView;
  tax: TaxBlockView;
  today: TodayView;
  direct: DirectView;
  yearly: YearlyView;
  subsidy: SubsidyView | null;
}

function yearAt(result: InvestmentResult, year: number): InvestmentYear {
  const row = result.years[Math.min(Math.max(year, 1), INVESTMENT_HORIZON_YEARS) - 1];
  if (!row) throw new RangeError(`no year ${year}`);
  return row;
}

/** Block 1 „Monatliche Bilanz" of year 1 (Nico's `render()`: the year's values ÷ 12). */
export function buildMonthlyBalance(result: InvestmentResult): MonthlyBalanceView {
  const mb = monthlyBalance(result, 1);
  const costs = mb.deductibleCostsCents + mb.nonDeductibleCostsCents;
  // „Tilgung als Kosten" off (Nico's `tilgungAlsKosten`): the principal is wealth building, not an expense.
  const principal = result.repaymentAsCost ? mb.principalCents : 0;
  // Derived from the rounded monthly figures so that both columns add up to the same total.
  const own = mb.interestCents + principal + costs - mb.rentCents - mb.taxEffectCents;
  const income: BalanceRow[] = [{ label: 'Kaltmiete', cents: mb.rentCents }];
  if (mb.taxEffectCents >= 0) income.push({ label: 'Steuervorteil', cents: mb.taxEffectCents });
  if (own > 0) income.push({ label: 'Eigenanteil (Zuzahlung)', cents: own });
  const expenses: BalanceRow[] = [
    { label: 'Zinsen', cents: mb.interestCents },
    ...(result.repaymentAsCost ? [{ label: 'Tilgung', cents: mb.principalCents }] : []),
    { label: 'Laufende Kosten', cents: costs },
  ];
  if (mb.taxEffectCents < 0) expenses.push({ label: 'Steuerlast', cents: -mb.taxEffectCents });
  if (own < 0) expenses.push({ label: 'Liquiditätsüberschuss', cents: -own });
  const sum = (rows: BalanceRow[]) => rows.reduce((total, row) => total + row.cents, 0);
  const taxTerm =
    mb.taxEffectCents >= 0
      ? `− Steuervorteil ${eur(mb.taxEffectCents)}`
      : `+ Steuerlast ${eur(-mb.taxEffectCents)}`;
  return {
    income,
    expenses,
    incomeTotalCents: sum(income),
    expenseTotalCents: sum(expenses),
    ownShareCents: own,
    headline: own > 0 ? 'Monatlicher Eigenanteil des Investors' : 'Monatlicher Liquiditätsüberschuss',
    formula: `Zinsen ${eur(mb.interestCents)}${result.repaymentAsCost ? ` + Tilgung ${eur(mb.principalCents)}` : ''} + Laufende Kosten ${eur(costs)} − Kaltmiete ${eur(mb.rentCents)} ${taxTerm} = ${sgn(own)} €`,
    repaymentNote: result.repaymentAsCost
      ? null
      : `Tilgung ${eur(mb.principalCents)} / Monat nicht als Kosten gerechnet (Vermögensaufbau)`,
  };
}

/** Everything the advisor view shows for the inputs and the horizon (10/20/30 years). */
export function buildAdvisorView(
  values: InvestmentInputValues,
  result: InvestmentResult,
  horizon: Horizon,
): AdvisorView {
  const row = yearAt(result, horizon);
  const year1 = yearAt(result, 1);
  const er = equityReturn(result, horizon, values.purchasePriceCents);
  const balance = buildMonthlyBalance(result);
  const own = balance.ownShareCents;
  const mb = monthlyBalance(result, 1);

  const loanNote = values.sellerPaysAcquisitionCosts
    ? '100 % inkl. NK'
    : values.purchasePriceCents > 0
      ? `${pct((result.loanCents / values.purchasePriceCents) * 100, 0)} des Kaufpreises`
      : '—';
  const financing: FinancingView = {
    priceCents: values.purchasePriceCents,
    pricePerSqmCents: values.areaSqm > 0 ? values.purchasePriceCents / values.areaSqm : null,
    acquisitionCostsPct: values.acquisitionCostsPct,
    acquisitionCostsCents: result.acquisitionCostsCents,
    totalInvestmentCents: result.totalInvestmentCents,
    grossYieldPct: result.grossYieldPct,
    loanCents: result.loanCents,
    loanNote,
    equityCents: result.equityCents,
    annuityMonthlyCents: result.annuityMonthlyCents,
    annuityNote: `Zins ${pct(values.interestPct, 2)} · Tilgung ${pct(values.repaymentPct, 2)}`,
    fixedInterestYears: values.fixedInterestYears,
    fixedInterestNote: `danach ${pct(values.followUpInterestPct, 2)}`,
    equityRatePaPct: er.ratePaWithPct,
  };

  const tax: TaxBlockView = {
    baseCents: result.depreciationBaseCents,
    manualDepreciation: values.depreciationManualCents !== null,
    depreciationPct: values.depreciationPct,
    usefulLifeYears:
      values.depreciationManualCents === null && values.depreciationPct > 0
        ? 100 / values.depreciationPct
        : null,
    depreciationYearCents: year1.depreciationCents,
    depreciationMonthCents: year1.depreciationCents / 12,
    taxableYearCents: year1.taxableResultCents,
    taxRatePct: values.taxRatePct,
    taxEffectYearCents: year1.taxEffectCents,
    taxEffectMonthCents: year1.taxEffectCents / 12,
    maintenanceExpenseCents: values.maintenanceExpenseCents,
    oneOffRefundCents: result.oneOffTaxRefundCents,
    additionalDepreciationYearCents: year1.additionalDepreciationCents,
  };

  const today: TodayView = {
    gainCents: row.equityCents - result.equityCents,
    explanation: `Objektwert nach ${horizon} Jahren abzüglich Restschuld und eingesetztem Eigenkapital von ${eur(result.equityCents)}. Nach zehn Jahren Haltedauer fällt auf den Verkauf keine Einkommensteuer an (§ 23 EStG).`,
    propertyValueCents: row.propertyValueCents,
    remainingDebtCents: row.remainingDebtCents,
    netSaleCents: row.equityCents,
    investedCents: er.investedCents,
    equityReturn: er,
  };

  const series = result.years.slice(0, horizon);
  const direct: DirectView = {
    monthlyPaymentCents: Math.max(0, own),
    monthlyPrincipalCents: mb.principalCents,
    netMonthlyCents: mb.principalCents - own,
    paymentsTotalCents: row.ownPaymentsCents,
    repaidTotalCents: row.repaidCents,
    equityWithoutAppreciationCents: er.returnWithoutAppreciationCents,
    paid: series.map((entry) => entry.ownPaymentsCents),
    repaid: series.map((entry) => entry.repaidCents),
  };

  const yearly: YearlyView = {
    debt: series.map((entry) => entry.remainingDebtCents),
    equity: series.map((entry) => entry.equityCents),
    value: series.map((entry) => entry.propertyValueCents),
  };

  const hasSubsidy =
    values.rentSubsidyCents > 0 ||
    values.rentSubsidyCycles > 0 ||
    values.renovationSubsidyCents > 0 ||
    values.maintenanceExpenseCents > 0;
  const subsidy: SubsidyView | null = hasSubsidy
    ? {
        rentSubsidyCents: values.rentSubsidyCents,
        cycles: values.rentSubsidyCycles,
        months: result.rentSubsidyMonths,
        rentSentence:
          result.rentSubsidyMonths > 0
            ? `Liquidität mit Soll-Miete bis Monat ${formatInteger(result.rentSubsidyMonths)}, danach Kaltmiete (Ist) mit Mietsteigerung`
            : null,
        renovationSubsidyCents: values.renovationSubsidyCents,
        maintenanceExpenseCents: values.maintenanceExpenseCents,
        oneOffRefundCents: result.oneOffTaxRefundCents,
      }
    : null;

  return { horizon, balance, financing, tax, today, direct, yearly, subsidy };
}

/**
 * Nico's `buildSummary()`: the plain-text summary for a message to the customer („Zusammenfassung kopieren",
 * D-350), line by line as in his calculator; the monthly own share always includes the principal, like his.
 */
export function buildTextSummary(
  values: InvestmentInputValues,
  result: InvestmentResult,
  horizon: Horizon,
  address: string | null,
): string {
  const m = result.monthly;
  const costs = m.nonRecoverableCostsCents;
  const own = m.interestCents + m.principalCents + costs - m.rentCents - m.taxEffectCents;
  const row = yearAt(result, horizon);
  const gain = row.propertyValueCents - row.remainingDebtCents - result.equityCents;
  const area = values.areaSqm;
  const areaText = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(area);
  return [
    'Wirtschaftlichkeitsberechnung',
    `Objekt: ${address ?? '—'}`,
    '',
    `Kaufpreis: ${eur(values.purchasePriceCents)} (${eur(area > 0 ? values.purchasePriceCents / area : 0)}/m², ${areaText} m²)`,
    `Kaufnebenkosten (${pct(values.acquisitionCostsPct, 1)}): ${eur(result.acquisitionCostsCents)}`,
    `Gesamtinvestition: ${eur(result.totalInvestmentCents)}`,
    `Eigenkapital: ${eur(result.equityCents)}${values.sellerPaysAcquisitionCosts ? ' (ohne EK / inkl. NK finanziert)' : ''}`,
    `Darlehen: ${eur(result.loanCents)} · ${pct(values.interestPct, 2)} Zins · ${pct(values.repaymentPct, 2)} Tilgung`,
    `Annuität: ${eur(result.annuityMonthlyCents)}/Monat`,
    '',
    `Kaltmiete: ${eur(m.rentCents)}/Monat`,
    `Steuervorteil: ${eur(result.taxEffectYear1Cents)}/Jahr (${eur(m.taxEffectCents)}/Monat)`,
    `Monatlicher Eigenanteil: ${eur(own)}`,
    `Monatliche Tilgung (Vermögensaufbau): ${eur(m.principalCents)}`,
    '',
    `Annahmen: ${pct(values.appreciationPct, 1)} Wertentwicklung p.a. · Mietsteigerung ${pct(values.rentIncreasePct, 1)} alle ${formatInteger(values.rentIncreaseIntervalMonths)} Monate`,
    `Objektwert nach ${horizon} J.: ${eur(row.propertyValueCents)} · Restschuld: ${eur(row.remainingDebtCents)}`,
    `Möglicher steuerfreier Gewinn nach ${horizon} Jahren: ${eur(gain)}`,
    '',
    'Unverbindliche Modellrechnung – erstellt mit dem DAMOKLES Immobilienrechner.',
  ].join('\n');
}

// --- customer view (Kundenansicht) ---------------------------------------------------------------------

export interface SplitView {
  totalCents: number;
  tenantCents: number;
  taxOfficeCents: number;
  ownCents: number;
  interestCents: number;
  principalCents: number;
}

export interface YearBar {
  year: number;
  selected: boolean;
  /** Heights as fractions (0–1) of the chart's upward area (78 %) / downward area (22 %). */
  tenant: number;
  taxOffice: number;
  own: number;
  appreciation: number;
  loss: number;
}

export interface StripBar {
  year: number;
  selected: boolean;
  tenant: number;
  taxOffice: number;
  own: number;
  ownCents: number;
}

export interface TaxView {
  /** Tax rent (Kaltmiete (Ist)) of year 1. */
  rentCents: number;
  /** Deductible running costs of year 1. */
  deductibleCents: number;
  interestCents: number;
  depreciationCents: number;
  cashCents: number;
  recognisedCents: number;
  lossCents: number;
  refundYearCents: number;
  refundMonthCents: number;
  buildingShareCents: number;
  /** How the AfA of question 07 comes about: „ergibt sich aus dem Gebäudeanteil von …" or the manual/Zusatz-AfA. */
  depreciationOrigin: string;
  principalYearCents: number;
  grossMonthlyCents: number;
  wageTaxOldCents: number;
  wageTaxNewCents: number;
  netOldCents: number;
  netNewCents: number;
  allowanceMonthlyCents: number;
  /** Share of the recognised expenses (0–1). */
  widths: { interest: number; deductible: number; depreciation: number; rent: number };
  incomeSource: 'contact' | 'assumption';
}

export interface CostRow {
  label: string;
  note: string;
  cents: number;
  deductible: boolean;
}

export interface RunningCostsView {
  rows: CostRow[];
  totalCents: number;
  deductibleCents: number;
  /** "Hausgeld mtl." of the unit (fact only, not calculated with). */
  serviceChargeCents: number | null;
  serviceChargeReserveCents: number | null;
  areaLabel: string;
  perSqmCents: number;
}

export interface ReserveView {
  monthlyCents: number;
  yearlyCents: number;
  tenYearsCents: number;
  perSqmYearCents: number | null;
}

export interface CalculatorView {
  result: InvestmentResult;
  empty: boolean;
  monthly: InvestmentResult['monthly'];
  /** Installment split of the selected installment year. */
  split: SplitView;
  strip: StripBar[];
  bars: YearBar[];
  saleYearRow: InvestmentYear;
  aheadSentence: string;
  breakEvenSentence: string;
  saleNote: string;
  saleTaxLabel: string;
  disclaimer: string;
  taxOfficeExplanation: string;
  tax: TaxView;
  runningCosts: RunningCostsView;
  reserve: ReserveView;
}

/** Assumed taxable income of question 07 when the self-disclosure has none. */
const ASSUMED_TAXABLE_INCOME_YEAR_CENTS = 96_000_00;

function splitOf(row: InvestmentYear): SplitView {
  return {
    totalCents: row.installment.totalCents,
    tenantCents: row.installment.tenantCents,
    taxOfficeCents: row.installment.taxOfficeCents,
    ownCents: row.installment.ownCents,
    interestCents: row.installment.interestCents,
    principalCents: row.installment.principalCents,
  };
}

/** Rent increase as the inputs state it: "15,0 % alle 36 Monate" / "keine". */
export function rentIncreaseText(values: InvestmentInputValues): string {
  if (values.rentIncreasePct <= 0 || values.rentIncreaseIntervalMonths <= 0) return 'keine Mietsteigerung';
  return `Mietsteigerung ${pct(values.rentIncreasePct)} alle ${formatInteger(values.rentIncreaseIntervalMonths)} Monate`;
}

/** Everything the customer view shows, derived from the inputs, the cost split and the context. */
export function buildView(
  values: InvestmentInputValues,
  split: CostSplit,
  context: CalculatorContext | null,
): CalculatorView {
  const result = calculateInvestment(values);
  const empty = values.purchasePriceCents <= 0 || values.rentCents <= 0;
  const monthly = result.monthly;

  const saleRow = yearAt(result, values.saleYear);
  const installmentRow = yearAt(result, values.installmentYear);
  const installment = splitOf(installmentRow);

  const strip: StripBar[] = result.years.slice(0, 15).map((row) => {
    const total = row.installment.totalCents || 1;
    return {
      year: row.year,
      selected: row.year === values.installmentYear,
      tenant: row.installment.tenantCents / total,
      taxOffice: row.installment.taxOfficeCents / total,
      own: row.installment.ownCents / total,
      ownCents: row.installment.ownCents,
    };
  });

  const maxUp = Math.max(...result.years.map((row) => row.repaidCents + Math.max(0, row.appreciationCents)));
  const maxDown = Math.max(...result.years.map((row) => Math.max(0, -row.appreciationCents))) || 1;
  const unit = Math.max(maxUp / 0.78, maxDown / 0.22) || 1;
  const bars: YearBar[] = result.years.map((row) => ({
    year: row.year,
    selected: row.year === values.saleYear,
    tenant: row.repaidByTenantCents / unit / 0.78,
    taxOffice: row.repaidByTaxOfficeCents / unit / 0.78,
    own: row.repaidFromOwnPocketCents / unit / 0.78,
    appreciation: Math.max(0, row.appreciationCents) / unit / 0.78,
    loss: Math.max(0, -row.appreciationCents) / unit / 0.22,
  }));

  const aheadYear = result.gainExceedsOwnPaymentsYear;
  const aheadSentence = aheadYear
    ? `Ab Jahr ${aheadYear} ist der Gewinn größer als alles, was Sie bis dahin aus eigener Tasche gezahlt haben.`
    : 'Innerhalb von 30 Jahren übersteigt der Gewinn Ihre Zuzahlungen nicht.';
  const breakEvenSentence = result.equityExceedsOwnPaymentsYear
    ? `Ab Jahr ${result.equityExceedsOwnPaymentsYear} ist Ihr Anteil größer als alles, was Sie eingezahlt haben.`
    : 'Innerhalb von 30 Jahren übersteigt Ihr Anteil das eingezahlte Geld nicht.';

  const sale = result.sale;
  const saleNote = sale.taxFree
    ? 'Nach zehn Jahren Haltedauer fällt auf den Verkauf keine Einkommensteuer an (§ 23 EStG) — weder auf die Wertsteigerung noch auf den durch die Miete getilgten Teil. Der Betrag fließt Ihnen in voller Höhe zu.'
    : `Innerhalb der zehnjährigen Spekulationsfrist ist der Verkaufsgewinn steuerpflichtig: Erlös abzüglich Kaufpreis plus der bereits genutzten Abschreibung von ${eur(sale.cumulativeDepreciationCents)}, versteuert mit ${pct(values.taxRatePct, 0)} — das sind ${eur(sale.saleTaxCents)}, die vom Erlös abgehen. Ab Jahr 10 entfällt diese Steuer.`;

  const lossMonthly = Math.abs(installmentRow.taxableResultCents) / 12;
  const taxOfficeExplanation = `So kommt der Betrag des Finanzamts zustande: Abschreibung ${eur((installmentRow.depreciationCents + installmentRow.additionalDepreciationCents) / 12)} und Zinsen ${eur(installmentRow.interestCents / 12)} pro Monat übersteigen die Mieteinnahme. Der steuerliche Verlust von ${eur(lossMonthly)} im Monat senkt Ihr zu versteuerndes Einkommen; bei ${pct(values.taxRatePct, 0)} Grenzsteuersatz ergibt das ${eur(installment.taxOfficeCents)} monatlich, ${eur(installmentRow.taxEffectCents)} im Jahr.`;

  // Frage 06/07: the tax waterfall of year 1 (tax rent) and the wage-tax comparison with the input tax rate.
  const year1 = yearAt(result, 1);
  const loss = Math.max(0, -year1.taxableResultCents);
  const cash = year1.interestCents + year1.deductibleCostsCents;
  // AfA of question 06/07 includes the Zusatz-AfA so that the waterfall adds up to the taxable result.
  const depreciation1 = year1.depreciationCents + year1.additionalDepreciationCents;
  const recognised = cash + depreciation1;
  const incomeMonthly = context?.contact?.netIncomeCents ?? null;
  const grossMonthly = incomeMonthly ?? ASSUMED_TAXABLE_INCOME_YEAR_CENTS / 12;
  const wageTaxOld = (grossMonthly * values.taxRatePct) / 100;
  const refundMonth = year1.taxEffectCents / 12;
  const tax: TaxView = {
    rentCents: year1.rentTaxCents,
    deductibleCents: year1.deductibleCostsCents,
    interestCents: year1.interestCents,
    depreciationCents: depreciation1,
    cashCents: cash,
    recognisedCents: recognised,
    lossCents: loss,
    refundYearCents: year1.taxEffectCents,
    refundMonthCents: refundMonth,
    buildingShareCents: result.depreciationBaseCents,
    depreciationOrigin: [
      values.depreciationManualCents === null
        ? `ergibt sich aus dem Gebäudeanteil von ${eur(result.depreciationBaseCents)}`
        : `ist als fester Betrag von ${eur(values.depreciationManualCents)} angesetzt`,
      year1.additionalDepreciationCents > 0
        ? `, davon ${eur(year1.additionalDepreciationCents)} Zusatz-AfA in den Jahren 1–${ADDITIONAL_DEPRECIATION_YEARS}`
        : '',
    ].join(''),
    principalYearCents: year1.principalCents,
    grossMonthlyCents: grossMonthly,
    wageTaxOldCents: wageTaxOld,
    wageTaxNewCents: wageTaxOld - refundMonth,
    netOldCents: grossMonthly - wageTaxOld,
    netNewCents: grossMonthly - wageTaxOld + refundMonth,
    allowanceMonthlyCents: loss / 12,
    widths: {
      interest: recognised > 0 ? year1.interestCents / recognised : 0,
      deductible: recognised > 0 ? year1.deductibleCostsCents / recognised : 0,
      depreciation: recognised > 0 ? depreciation1 / recognised : 0,
      rent: recognised > 0 ? Math.min(1, year1.rentTaxCents / recognised) : 0,
    },
    incomeSource: incomeMonthly === null ? 'assumption' : 'contact',
  };

  // Frage 08: the running costs of the inputs (the unit's Hausgeld is a fact only).
  const area = values.areaSqm;
  const rows: CostRow[] = [
    {
      label: 'SE-Verwaltung',
      note: 'Verwaltung Ihrer Wohnung: Mietvertrag, Nebenkostenabrechnung, Mieterwechsel.',
      cents: split.sevCents,
      deductible: true,
    },
    {
      label: 'WEG-Verwaltung',
      note: 'Honorar des WEG-Verwalters für Abrechnung, Versammlung, Beschlussumsetzung.',
      cents: split.wegCents,
      deductible: true,
    },
    {
      label: 'Sonstige nicht umlagefähige Kosten',
      note: 'Kosten, die nicht auf den Mieter umgelegt werden können.',
      cents: split.otherCostsCents,
      deductible: true,
    },
    {
      label: 'Instandhaltungsrücklage',
      note: 'Liegt auf dem Konto der Gemeinschaft und finanziert Dach, Fassade, Heizung und Aufzug (§ 19 WEG).',
      cents: values.nonDeductibleCostsCents,
      deductible: false,
    },
  ];
  const totalCosts = rows.reduce((sum, row) => sum + row.cents, 0);
  const runningCosts: RunningCostsView = {
    rows,
    totalCents: totalCosts,
    deductibleCents: deductibleSum(split),
    serviceChargeCents: context?.unit.serviceChargeCents ?? null,
    serviceChargeReserveCents: context?.unit.reserveShareCents ?? null,
    areaLabel: `${formatInteger(Math.round(area))}${NBSP}m²`,
    perSqmCents: area > 0 ? totalCosts / area : 0,
  };

  // Frage 09: the maintenance reserve of the inputs.
  const reserveMonthly = values.nonDeductibleCostsCents;
  const reserve: ReserveView = {
    monthlyCents: reserveMonthly,
    yearlyCents: reserveMonthly * 12,
    tenYearsCents: reserveMonthly * 120,
    perSqmYearCents: area > 0 ? (reserveMonthly * 12) / area : null,
  };

  return {
    result,
    empty,
    monthly,
    split: installment,
    strip,
    bars,
    saleYearRow: saleRow,
    aheadSentence,
    breakEvenSentence,
    saleNote,
    saleTaxLabel: sale.taxFree ? 'Steuerfrei' : 'Nach Steuer auf den Verkaufsgewinn',
    disclaimer: `Unverbindliche Modellrechnung. Wertentwicklung ${pct(values.appreciationPct)} pro Jahr, ${rentIncreaseText(values)}, ${values.depreciationManualCents === null ? `AfA linear ${pct(values.depreciationPct, 2)} auf den Gebäudeanteil` : `AfA manuell ${eur(values.depreciationManualCents)} pro Jahr`}, persönlicher Steuersatz ${pct(values.taxRatePct, 0)}. Keine Steuer- oder Anlageberatung.`,
    taxOfficeExplanation,
    tax,
    runningCosts,
    reserve,
  };
}

// --- advisor view v2 (Berateransicht nach DokuMe, owner decision 02.11.2026) ----------------------------

/** Purchase year the advisor view counts calendar years from: the current year in Europe/Berlin. */
export function advisorPurchaseYear(now: Date = new Date()): number {
  return Number(toBerlinDateKey(now).slice(0, 4));
}

/** Calendar year of the n-th year after the purchase (year 1 = the purchase year). */
export function calendarYear(purchaseYear: number, year: number): number {
  return purchaseYear + year - 1;
}

/** First year whose monthly own share (`monthlyBalance`) is a surplus (< 0); null when none within 30 years. */
export function firstSurplusYear(result: InvestmentResult): number | null {
  for (const row of result.years) {
    if (monthlyOwnShare(result, row.year) < 0) return row.year;
  }
  return null;
}

/** Lowest point of the cumulative cash flow (the year with the most paid in so far). */
export function cumulativeMinimum(result: InvestmentResult): { year: number; cents: number } {
  let low = { year: 1, cents: yearAt(result, 1).cumulativeCashflowCents };
  for (const row of result.years) {
    if (row.cumulativeCashflowCents < low.cents) low = { year: row.year, cents: row.cumulativeCashflowCents };
  }
  return low;
}

/** First year from which the cumulative cash flow stays above 0 up to year 30; null when it ends at or below 0. */
export function cumulativeTurnYear(result: InvestmentResult): number | null {
  let lastNonPositive = 0;
  for (const row of result.years) if (row.cumulativeCashflowCents <= 0) lastNonPositive = row.year;
  return lastNonPositive >= INVESTMENT_HORIZON_YEARS ? null : lastNonPositive + 1;
}

export type LedgerKey =
  'rent' | 'taxBenefit' | 'ownShare' | 'principal' | 'interest' | 'costs' | 'tax' | 'surplus';

export interface LedgerRow {
  key: LedgerKey;
  label: string;
  cents: number;
}

/** „Betrachtetes Jahr": income and expenses of one year (annual sums); both sides add up to the same total. */
export interface YearLedger {
  year: number;
  income: LedgerRow[];
  expenses: LedgerRow[];
  totalCents: number;
  /** Annual own share: > 0 the customer pays, < 0 surplus (same rule as `monthlyBalance`). */
  ownShareCents: number;
  ownShareMonthlyCents: number;
  /** (Interest + principal + running costs) ÷ 12, the principal always included. */
  grossMonthlyCents: number;
  repaymentAsCost: boolean;
  /** The year's principal (shown as a note when it is not counted as a cost). */
  principalCents: number;
}

/** The year's ledger; with „Tilgung als Kosten" off the principal is left out like `monthlyBalance` does. */
export function yearLedger(result: InvestmentResult, year: number): YearLedger {
  const row = yearAt(result, year);
  const asCost = result.repaymentAsCost;
  const costs = row.deductibleCostsCents + row.nonDeductibleCostsCents;
  const principal = asCost ? row.principalCents : 0;
  const tax = row.taxEffectCents;
  const own = row.interestCents + principal + costs - row.rentCents - tax;
  const income: LedgerRow[] = [{ key: 'rent', label: 'Kaltmiete', cents: row.rentCents }];
  if (tax > 0) income.push({ key: 'taxBenefit', label: 'Steuervorteil', cents: tax });
  if (own > 0) income.push({ key: 'ownShare', label: 'Ihr Eigenanteil', cents: own });
  const expenses: LedgerRow[] = [];
  if (asCost) expenses.push({ key: 'principal', label: 'Tilgung', cents: row.principalCents });
  expenses.push({ key: 'interest', label: 'Zinsen', cents: row.interestCents });
  expenses.push({ key: 'costs', label: 'Kosten', cents: costs });
  if (tax < 0) expenses.push({ key: 'tax', label: 'Steuern auf Mietgewinn', cents: -tax });
  if (own < 0) expenses.push({ key: 'surplus', label: 'Überschuss an Sie', cents: -own });
  return {
    year: row.year,
    income,
    expenses,
    totalCents: income.reduce((sum, entry) => sum + entry.cents, 0),
    ownShareCents: own,
    ownShareMonthlyCents: Math.round(own / 12),
    grossMonthlyCents: Math.round((row.interestCents + row.principalCents + costs) / 12),
    repaymentAsCost: asCost,
    principalCents: row.principalCents,
  };
}

/** „Ihr Weg mit dieser Wohnung": one sentence from the first surplus year. */
export function journeySentence(result: InvestmentResult, purchaseYear: number): string {
  const surplus = firstSurplusYear(result);
  if (surplus === null) return 'In den ersten 30 Jahren zahlen Sie jeden Monat zu.';
  if (surplus === 1)
    return 'Die Wohnung trägt sich vom ersten Jahr an selbst und bringt Ihnen jeden Monat Geld.';
  return `Am Anfang zahlen Sie zu. Die Miete steigt, die Zinsen sinken. Ab ${calendarYear(purchaseYear, surplus)}, im ${surplus}. Jahr, trägt sich die Wohnung selbst und bringt Ihnen jeden Monat Geld.`;
}

/** Note under „Aufsummiert": how far the cumulative cash flow goes down and when it turns positive. */
export function cumulativeSentence(result: InvestmentResult, purchaseYear: number): string {
  const low = cumulativeMinimum(result);
  const turn = cumulativeTurnYear(result);
  if (low.cents >= 0) return 'Sie liegen vom ersten Jahr an im Plus.';
  const paid = eur(-low.cents);
  if (low.year >= INVESTMENT_HORIZON_YEARS) {
    return `In den ersten 30 Jahren zahlen Sie insgesamt ${paid} zu.`;
  }
  const until = `Bis ${calendarYear(purchaseYear, low.year)} zahlen Sie insgesamt ${paid} zu.`;
  if (turn === null) {
    return `${until} Danach holen die Überschüsse das zurück, in den ersten 30 Jahren aber nicht ganz.`;
  }
  return `${until} Danach holen die Überschüsse das zurück, ab ${calendarYear(purchaseYear, turn)} liegen Sie im Plus.`;
}

/** Note under „Vermögen" for the selected year. */
export function wealthSentence(result: InvestmentResult, year: number, purchaseYear: number): string {
  const row = yearAt(result, year);
  return `Ihr Vermögen ist der Abstand zwischen Wohnungswert und Restschuld: ${eur(row.equityCents)} im Jahr ${calendarYear(purchaseYear, row.year)}.`;
}

export interface SaleOutcome {
  horizon: Horizon;
  label: string;
  returnCents: number;
  investedCents: number;
  ratePaPct: number | null;
  caption: string;
}

/** „Wenn Sie verkaufen": `equityReturn` with appreciation after 10, 20 and 30 years. */
export function saleOutcomes(result: InvestmentResult, purchasePriceCents: number): SaleOutcome[] {
  return HORIZONS.map((horizon) => {
    const er = equityReturn(result, horizon, purchasePriceCents);
    const invested = er.investedCents;
    const caption =
      invested <= 0
        ? `die Wohnung hat Ihnen bis dahin ${eur(-invested)} mehr gebracht, als Sie eingezahlt haben`
        : er.ratePaWithPct === null
          ? `bei ${eur(invested)} eingezahlt`
          : `bei ${eur(invested)} eingezahlt · ${pct(er.ratePaWithPct, 2)} pro Jahr`;
    return {
      horizon,
      label: horizon === 10 ? 'Nach 10 Jahren, steuerfrei' : `Nach ${horizon} Jahren`,
      returnCents: er.returnWithAppreciationCents,
      investedCents: invested,
      ratePaPct: er.ratePaWithPct,
      caption,
    };
  });
}

/** „steigt um 15,0 % alle 3 Jahre" — the rent increase of the inputs in plain words. */
export function rentIncreaseLine(values: InvestmentInputValues): string {
  const months = values.rentIncreaseIntervalMonths;
  if (values.rentIncreasePct <= 0 || months <= 0) return 'ohne Mietsteigerung gerechnet';
  const every =
    months === 12
      ? 'jedes Jahr'
      : months % 12 === 0
        ? `alle ${formatInteger(months / 12)} Jahre`
        : `alle ${formatInteger(months)} Monate`;
  return `steigt um ${pct(values.rentIncreasePct)} ${every}`;
}

const LAND_TRANSFER_SOURCE = /Grunderwerbsteuer [^(]*\(([^)]+)\)/;

/** The property's federal state as named in the source line of „Kaufnebenkosten" (D-355); null when unknown. */
export function federalStateOf(context: CalculatorContext): string | null {
  const source = context.sources.find((entry) => entry.field === 'acquisitionCostsPct')?.source ?? '';
  const short = LAND_TRANSFER_SOURCE.exec(source)?.[1];
  if (short === undefined) return null;
  const state = PROPERTY_STATES.find((key) => propertyStateShortLabels[key] === short);
  return state === undefined ? null : propertyStateLabels[state];
}

/** The one-off depreciation total: the base over the useful life (linear) or 30 years of the manual amount. */
export function depreciationTotal(
  values: InvestmentInputValues,
  result: InvestmentResult,
): { cents: number; caption: string } {
  if (values.depreciationManualCents === null && values.depreciationPct > 0) {
    const years = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(
      100 / values.depreciationPct,
    );
    return { cents: result.depreciationBaseCents, caption: `über ${years} Jahre` };
  }
  const total = result.years.reduce((sum, row) => sum + row.depreciationCents, 0);
  return { cents: total, caption: 'in 30 Jahren' };
}

/** The advisor view's disclaimer: the model assumptions plus the conditions after the fixed-interest period. */
export function advisorDisclaimer(values: InvestmentInputValues): string {
  const depreciation =
    values.depreciationManualCents === null
      ? `Abschreibung (AfA) linear ${pct(values.depreciationPct, 2)} auf den Gebäudeanteil`
      : `Abschreibung (AfA) fest ${eur(values.depreciationManualCents)} pro Jahr`;
  return `Unverbindliche Modellrechnung. Wertentwicklung ${pct(values.appreciationPct)} pro Jahr, ${rentIncreaseText(values)}, ${depreciation}, persönlicher Steuersatz ${pct(values.taxRatePct, 0)}. Nach Ende der Zinsbindung (${formatInteger(values.fixedInterestYears)} Jahre) sind ${pct(values.followUpInterestPct, 2)} Zins und ${pct(values.followUpRepaymentPct, 2)} Tilgung angenommen. Keine Steuer- oder Anlageberatung.`;
}

/** Monthly own share of a year (`monthlyBalance`): > 0 the customer pays, < 0 surplus. */
export function monthlyOwnShare(result: InvestmentResult, year: number): number {
  return monthlyBalance(result, year).ownShareCents;
}
